import React from 'react';

import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import {
  useBulkAddDocumentsToTeam,
  useBulkRemoveDocumentsFromTeam,
  useTeams,
} from 'app/teams/api';
import {RemoveFromTeamButton} from 'app/teams/components/TeamDetailPage/components/TeamListItem';
import {isMemberTeam} from 'app/teams/selectors';
import {convertLocationParamsToFetchParams} from 'app/teams/utils';

import {AddToTeamPopover} from './AddToTeamPopover';

import type {GenericAttachedItemProps} from './AddToTeamPopover';
import type {OverlayProps} from 'app/general/overlays';
import type {
  ProcedureSerializedType,
  TeamMembershipSerializedType,
  TeamSerializedType,
} from 'sweet-types';

interface Props {
  overlayProps: OverlayProps;
  documentURLs: Array<string>;
  documentObjectURLs: Array<string>;
  onSuccess: () => Promise<unknown>;
  teamMemberships: ProcedureSerializedType['team_memberships'];
}

export const AddDocumentsToTeamPopover = ({
  overlayProps,
  documentURLs,
  documentObjectURLs,
  teamMemberships,
  onSuccess,
}: Props) => {
  const {data: attached} = useTeams(
    convertLocationParamsToFetchParams({
      document_url: documentObjectURLs.join(','),
    }),
    {refetchOnMountOrArgChange: false}
  );

  const [addDocumentsToTeam] = useBulkAddDocumentsToTeam();
  const [removeDocumentsFromTeam] = useBulkRemoveDocumentsFromTeam();

  return (
    <AddToTeamPopover
      overlayProps={overlayProps}
      attached={attached?.results}
      itemURL={''}
      onSuccess={onSuccess}
      AttachedItemComponent={AttachedItemComponent}
      attachedItemComponentProps={{teamMemberships}}
      addItemToTeam={(team) => {
        return addDocumentsToTeam({
          team: team.url,
          documents: documentURLs,
        }).unwrap();
      }}
      removeItemsFromTeam={(team) => {
        return removeDocumentsFromTeam({
          team: team.url,
          documents: documentURLs,
        }).unwrap();
      }}
    />
  );
};

interface OwnProps {
  teamMemberships: Array<TeamMembershipSerializedType>;
}

interface InjectedProps {
  onRemove: (item: TeamSerializedType) => Promise<unknown>;
}

function AttachedItemComponent({
  item: team,
  onRemove,
  teamMemberships,
}: GenericAttachedItemProps & InjectedProps & OwnProps) {
  const membership = isMemberTeam(teamMemberships, team);
  return (
    <>
      <FullHTMLLink
        html_url={team.html_url}
        className="text-white text-truncate py-2"
      >
        {team.name}
      </FullHTMLLink>
      <RemoveFromTeamButton
        iconClassName="text-white"
        onRemove={() => onRemove(team)}
        contentType="document"
        membership={membership}
      />
    </>
  );
}
